import { axiosFieldValidationErrorToFormErrors, hasFieldValidationError, VerifiedEmailsResponse } from '@gofranz/common';
import { parseApiError } from '@gofranz/common-components';
import { Form, FormsIntegrationsQueryParams, FormsIntegrationsResponse, IntegrationsApiResponse, IntegrationsQueryParams, NewFormsIntegration, NewFormsRecipient, UpdateForm } from '@gofranz/formshive-common';
import { Accordion, Alert, Anchor, Button, Group, Text, Title } from '@mantine/core';
import {
  IconAlertTriangle,
  IconChecklist,
  IconMail,
  IconMailForward,
  IconMessageCircle,
  IconSettings,
  IconWebhook,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clearField } from '../../lib/clear-field';
import { FormSpec, parseAndValidateFormSpec } from '../../lib/form-specs';
import { validateTemplateString } from '../../lib/template-validation';
import { useRustyState } from '../../state';
import { FormsIntegrationDetail } from '../FormIntegrations/Detail';
import { FormsRecipientDetail } from '../FormsRecipient/Detail';
import { AutoResponse } from './AutoResponse';
import { FormFields, FormFieldsValues } from './Common';
import { FormSpecifications } from './FormSpecification';

// Types for distributed state
export interface FormDataRef {
  // FormFields section
  title: string;
  filter_spam: boolean;
  check_challenge: boolean;
  redirect_url: string | undefined;
  // FormSpecifications section
  check_specs: boolean;
  specs: string | undefined;
  // AutoResponse section
  auto_response_enabled: boolean;
  auto_response_subject: string | undefined;
  auto_response_text: string | undefined;
}

export interface EditFormProps {
  submitFormCb: (id: string, updatedForm: UpdateForm) => Promise<void>;
  form: Form;
  formSubmitUrl: string;
  formChallengeUrl: string;
  formChallengeUrlNoTrack?: string;

  // Hoisted from parent - parsed once
  parsedSpec: FormSpec | null;
  hasFormSpec: boolean;
  hasFileField: boolean;

  submitFormRecipientCb: (newForm: NewFormsRecipient) => Promise<void>;
  getVerifiedEmails: () => Promise<VerifiedEmailsResponse>;
  getFormVerifiedEmails: () => Promise<VerifiedEmailsResponse>;
  deleteRecipientCp: (formId: string, recipientId: string) => Promise<void>;

  submitFormIntegrationCb: (newForm: NewFormsIntegration) => Promise<void>;
  getIntegrations: (params: IntegrationsQueryParams) => Promise<IntegrationsApiResponse>;
  getFormIntegrations: (
    id: string,
    query: FormsIntegrationsQueryParams
  ) => Promise<FormsIntegrationsResponse>;
  deleteFormIntegrationCb: (formId: string, integrationId: string) => Promise<void>;

  onFormDataRef?: (ref: React.RefObject<FormDataRef>) => void;
  onOpenChat?: () => void;
}

export function EditForm(props: EditFormProps) {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<{ title: string, message: string } | null>(null);
  const [hasEmailRecipients, setHasEmailRecipients] = useState(false);

  // AutoResponse validation state
  const [validateSubject, setValidateSubject] = useState(false);
  const [validateText, setValidateText] = useState(false);

  // ARCHITECTURAL CHANGE: Use ref to collect form data from distributed child components
  // Each section owns its state; this ref aggregates for save operations
  const formDataRef = useRef<FormDataRef>({
    title: props.form.title,
    filter_spam: props.form.filter_spam,
    check_challenge: props.form.check_challenge,
    redirect_url: props.form.redirect_url || undefined,
    check_specs: props.form.check_specs,
    specs: props.form.specs || undefined,
    auto_response_enabled: props.form.auto_response_enabled,
    auto_response_subject: props.form.auto_response_subject || undefined,
    auto_response_text: props.form.auto_response_text || undefined,
  });

  // Original values for dirty comparison (immutable reference to initial props)
  const originalValuesRef = useRef<FormDataRef>({
    title: props.form.title,
    filter_spam: props.form.filter_spam,
    check_challenge: props.form.check_challenge,
    redirect_url: props.form.redirect_url || undefined,
    check_specs: props.form.check_specs,
    specs: props.form.specs || undefined,
    auto_response_enabled: props.form.auto_response_enabled,
    auto_response_subject: props.form.auto_response_subject || undefined,
    auto_response_text: props.form.auto_response_text || undefined,
  });

  // Expose formDataRef to parent for chat assistant
  useEffect(() => {
    props.onFormDataRef?.(formDataRef);
  }, [props.onFormDataRef]);

  // Trigger re-render when dirty state might have changed
  const [dirtyCheckCounter, setDirtyCheckCounter] = useState(0);

  // Compute dirty state by comparing current values against original
  const isDirty = useMemo(() => {
    const current = formDataRef.current;
    const original = originalValuesRef.current;
    return (
      current.title !== original.title ||
      current.filter_spam !== original.filter_spam ||
      current.check_challenge !== original.check_challenge ||
      current.redirect_url !== original.redirect_url ||
      current.check_specs !== original.check_specs ||
      current.specs !== original.specs ||
      current.auto_response_enabled !== original.auto_response_enabled ||
      current.auto_response_subject !== original.auto_response_subject ||
      current.auto_response_text !== original.auto_response_text
    );
  }, [dirtyCheckCounter]);

  // For form field errors (set on submit failure)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Check if form has email recipients
  useEffect(() => {
    const checkEmailRecipients = async () => {
      try {
        const data = await props.getFormVerifiedEmails();
        setHasEmailRecipients(data.data.length > 0);
      } catch (e) {
        console.error('Error checking email recipients:', e);
        setHasEmailRecipients(false);
      }
    };
    checkEmailRecipients();
  }, [props.form.id, props.getFormVerifiedEmails]);

  // Function to update email recipients count (for FormsRecipientDetail to call)
  const updateEmailRecipientsCount = async () => {
    try {
      const data = await props.getFormVerifiedEmails();
      setHasEmailRecipients(data.data.length > 0);
    } catch (e) {
      console.error('Error updating email recipients count:', e);
      setHasEmailRecipients(false);
    }
  };

  // Compute AutoResponse data (memoized, only recomputes when parsedSpec changes)
  const autoResponseData = useMemo(() => {
    // Compute available variables based on form spec
    const availableVariables = props.hasFormSpec && props.parsedSpec?.fields
      ? Object.entries(props.parsedSpec.fields).map(([fieldName, field]) => ({
          name: fieldName,
          description: field.label || `Field: ${fieldName}`,
        }))
      : [
          { name: 'name', description: "Submitter's name" },
          { name: 'email', description: "Submitter's email address" },
          { name: 'message', description: 'Message content' },
          { name: 'company', description: 'Company/organization name' },
          { name: 'phone', description: 'Phone number' },
          { name: 'subject', description: 'Subject or topic' },
        ];

    // Generate a single complete email template example
    const nameVar = availableVariables[0]?.name || 'name';
    const templateExample = props.hasFormSpec
      ? [
          `Hello {{ ${nameVar} | "there" }},`,
          '',
          'Thank you for reaching out! We\'ve received your submission and will review it shortly.',
          '',
          ...(availableVariables.length > 2
            ? [`Here\'s a summary of what you sent us:`,
               ...availableVariables.slice(1, 4).map(v => `- ${v.description}: {{ ${v.name} }}`),
               '']
            : []),
          'We\'ll get back to you as soon as possible.',
          '',
          'Best regards,',
          'The Team',
        ].join('\n')
      : [
          'Hello {{ name | "there" }},',
          '',
          'Thank you for reaching out! We\'ve received your message and will get back to you shortly.',
          '',
          'If you have any urgent questions, don\'t hesitate to call us directly.',
          '',
          'Best regards,',
          'The Team',
        ].join('\n');

    // Check if email field exists
    const hasEmailField = props.hasFormSpec && props.parsedSpec?.fields
      ? Object.values(props.parsedSpec.fields).some(
          (field) => field.field === 'email' || field.name.toLowerCase().includes('email')
        )
      : false;

    return {
      availableVariables,
      templateExample,
      hasEmailField,
    };
  }, [props.hasFormSpec, props.parsedSpec]);

  // Track auto_response values for validation (separate from ref to trigger validation useMemo)
  const [autoResponseSubjectForValidation, setAutoResponseSubjectForValidation] = useState(
    props.form.auto_response_subject || ''
  );
  const [autoResponseTextForValidation, setAutoResponseTextForValidation] = useState(
    props.form.auto_response_text || ''
  );

  // Memoized template validations - only validate when flag is true
  const EMPTY_VALIDATION = useMemo(() => ({ isValid: true, errors: [], warnings: [] }), []);

  const subjectValidation = useMemo(() => {
    if (!validateSubject || !autoResponseSubjectForValidation) {
      return EMPTY_VALIDATION;
    }
    return validateTemplateString(autoResponseSubjectForValidation);
  }, [autoResponseSubjectForValidation, validateSubject, EMPTY_VALIDATION]);

  const textValidation = useMemo(() => {
    if (!validateText || !autoResponseTextForValidation) {
      return EMPTY_VALIDATION;
    }
    return validateTemplateString(autoResponseTextForValidation);
  }, [autoResponseTextForValidation, validateText, EMPTY_VALIDATION]);

  // Increment to trigger reset in children
  const [resetCounter, setResetCounter] = useState(0);

  const cancelChanges = useCallback(() => {
    // Reset the ref to original values (copy from originalValuesRef)
    formDataRef.current = { ...originalValuesRef.current };
    setError(null);
    setFieldErrors({});
    // Trigger dirty re-check (will be false since we reset to original)
    setDirtyCheckCounter((c) => c + 1);
    // Trigger children to reset by incrementing counter
    setResetCounter((c) => c + 1);
  }, []);

  const submitForm = useCallback(async () => {
    const data = formDataRef.current;

    if (data.specs && data.specs.trim() !== '') {
      const specIsValid = parseAndValidateFormSpec(data.specs);
      if (!specIsValid.isValid) {
        let message = '';
        specIsValid.errors.forEach((value, key) => {
          message += `${key}: ${value}\n`;
        });
        setError({
          title: 'Form Specs Validation Error',
          message,
        });
        return;
      }
    }
    setError(null);
    const updatedForm: UpdateForm = {
      title: data.title,
      filter_spam: data.filter_spam,
      check_challenge: data.check_challenge,
      check_specs: data.check_specs,
      specs: data.specs,
      redirect_url: clearField(data.redirect_url),
      auto_response_enabled: data.auto_response_enabled,
      auto_response_subject: clearField(data.auto_response_subject),
      auto_response_text: clearField(data.auto_response_text),
    };
    setIsBusy(true);
    try {
      await props.submitFormCb(props.form.id, updatedForm);
      // Update original values to current values (now saved)
      originalValuesRef.current = { ...formDataRef.current };
      // Trigger dirty re-check (will be false since original now matches current)
      setDirtyCheckCounter((c) => c + 1);
    } catch (e) {
      setError({
        ...parseApiError(e),
      });
      if (hasFieldValidationError(e)) {
        const errors = axiosFieldValidationErrorToFormErrors(e);
        console.log(errors);
        setFieldErrors(errors);
      }
    } finally {
      setIsBusy(false);
    }
  }, [props.form.id, props.submitFormCb]);

  // Stable callbacks for child components to update the ref
  const handleFormFieldsChange = useCallback((values: Partial<FormFieldsValues>) => {
    Object.assign(formDataRef.current, values);
    // Trigger dirty state re-computation
    setDirtyCheckCounter((c) => c + 1);
  }, []);

  const handleSpecsChange = useCallback((specs: string | undefined, checkSpecs: boolean) => {
    formDataRef.current.specs = specs;
    formDataRef.current.check_specs = checkSpecs;
    // Trigger dirty state re-computation
    setDirtyCheckCounter((c) => c + 1);
  }, []);

  const handleAutoResponseChange = useCallback((field: string, value: any) => {
    (formDataRef.current as any)[field] = value;
    // Trigger dirty state re-computation
    setDirtyCheckCounter((c) => c + 1);
    // Update validation state for subject/text
    if (field === 'auto_response_subject') {
      setAutoResponseSubjectForValidation(value || '');
    } else if (field === 'auto_response_text') {
      setAutoResponseTextForValidation(value || '');
    }
  }, []);

  return (
    <>
      <Group justify="space-between" mb="sm">
        <Title order={2}>
          {t('formEdit.settings')}
        </Title>
        {props.onOpenChat && (
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconMessageCircle size={16} />}
            onClick={props.onOpenChat}
          >
            {t('chat.openAssistant')}
          </Button>
        )}
      </Group>

      {/* Warning for encrypted forms with file uploads */}
      {props.form.encryption_mode !== 'none' && props.hasFileField && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title={t('formEdit.fileUploadEncryptionWarningTitle')}
          color="yellow"
          mb="md"
        >
          {t('formEdit.fileUploadEncryptionWarning')}
        </Alert>
      )}

      <Accordion variant="contained">
        <Accordion.Item key={`accordion-${1}`} value="form-fields">
          <Accordion.Control>
            <Group wrap="nowrap">
              <IconSettings size={20} />
              <div>
                <Text>{t('formEdit.general')}</Text>
                <Text size="sm" c="dimmed" fw={400}>
                  {t('formEdit.generalDescription')}
                </Text>
              </div>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <FormFields
              isEditing={true}
              isBusy={isBusy}
              hasError={error}
              fieldErrors={fieldErrors}
              submitCb={submitForm}
              formSubmitUrl={props.formSubmitUrl}
              initialValues={{
                title: props.form.title,
                filter_spam: props.form.filter_spam,
                check_challenge: props.form.check_challenge,
                redirect_url: props.form.redirect_url || undefined,
                auto_response_enabled: props.form.auto_response_enabled,
              }}
              onValuesChange={handleFormFieldsChange}
              hasUnsavedChanges={isDirty}
              onCancel={cancelChanges}
              resetKey={resetCounter}
            />
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item key={`accordion-${2}`} value="specifications">
          <Accordion.Control>
            <Group wrap="nowrap">
              <IconChecklist size={20} />
              <div>
                <Text>{t('formEdit.formFieldsOptional')}</Text>
                <Text size="sm" c="dimmed">
                  {t('formEdit.formFieldsDescription')}{' '}
                  <Anchor href="/docs">{t('formEdit.readMore')}</Anchor>
                </Text>
              </div>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <FormSpecifications
              isEditing={true}
              isBusy={isBusy}
              hasError={error}
              submitSection={submitForm}
              initialSpecs={props.form.specs || undefined}
              initialCheckSpecs={props.form.check_specs}
              onSpecsChange={handleSpecsChange}
              formSubmitUrl={props.formSubmitUrl}
              formChallengeUrl={props.formChallengeUrl}
              formChallengeUrlNoTrack={props.formChallengeUrlNoTrack}
              hasUnsavedChanges={isDirty}
              onCancel={cancelChanges}
              parsedSpec={props.parsedSpec}
              hasFormSpec={props.hasFormSpec}
              resetKey={resetCounter}
            />
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item key={`accordion-${3}`} value="forwarding">
          <Accordion.Control>
            <Group wrap="nowrap">
              <IconMailForward size={20} />
              <div>
                <Text>{t('formEdit.emailNotifications')}</Text>
                <Text size="sm" c="dimmed" fw={400}>
                  {t('formEdit.emailNotificationsDescription')}
                </Text>
              </div>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <FormsRecipientDetail
              form={props.form}
              submitFormCb={props.submitFormRecipientCb}
              getVerifiedEmails={useRustyState.getState().getAndSetVerifiedEmails}
              getFormVerifiedEmails={props.getFormVerifiedEmails}
              deleteRecipientCp={props.deleteRecipientCp}
              onRecipientsChange={updateEmailRecipientsCount}
            />
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item key={`accordion-${4}`} value="autoresponse">
          <Accordion.Control>
            <Group wrap="nowrap">
              <IconMail size={20} />
              <div>
                <Text>Autoresponder</Text>
                <Text size="sm" c="dimmed" fw={400}>
                  Send automated responses to form submitters
                </Text>
              </div>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <AutoResponse
              isBusy={isBusy}
              hasError={error}
              submitCb={submitForm}
              hasUnsavedChanges={isDirty}
              onCancel={cancelChanges}
              hasEmailRecipients={hasEmailRecipients}
              hasFormSpec={props.hasFormSpec}
              availableVariables={autoResponseData.availableVariables}
              templateExample={autoResponseData.templateExample}
              hasEmailField={autoResponseData.hasEmailField}
              subjectValidation={subjectValidation}
              textValidation={textValidation}
              onValidateSubject={setValidateSubject}
              onValidateText={setValidateText}
              formValues={{
                check_challenge: props.form.check_challenge,
                check_specs: props.form.check_specs,
                auto_response_enabled: props.form.auto_response_enabled,
                auto_response_subject: props.form.auto_response_subject,
                auto_response_text: props.form.auto_response_text,
              }}
              onFormChange={handleAutoResponseChange}
              resetKey={resetCounter}
              onSendTest={async (email: string) => {
                await useRustyState.getState().api.sendAutoResponseTest(props.form.id, email);
              }}
              userEmail={useRustyState.getState().verifiedEmails.find((e) => e.is_verified)?.email}
            />
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item key={`accordion-${5}`} value="integrations">
          <Accordion.Control>
            <Group wrap="nowrap">
              <IconWebhook size={20} />
              <div>
                <Text>{t('formEdit.integrations')}</Text>
                <Text size="sm" c="dimmed" fw={400}>
                  {t('formEdit.integrationsDescription')}
                </Text>
              </div>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <FormsIntegrationDetail
              form={props.form}
              submitFormCb={props.submitFormIntegrationCb}
              getIntegrations={props.getIntegrations}
              getFormIntegrations={props.getFormIntegrations}
              deleteFormIntegrationCb={props.deleteFormIntegrationCb}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </>
  );
}
